import { CriteriaCertificationTypeEnumValue } from 'enums/CriteriaCertificationTypeEnum';
import { getLatestSubmission } from 'services/Utilities/certificationSubmissionSummaryUtils';

export const normalizeCollection = collection => {
  if (!collection) {
    return [];
  }

  if (Array.isArray(collection)) {
    return collection;
  }

  if (Array.isArray(collection.items)) {
    return collection.items;
  }

  return [];
};

export const getCriteriaCertificationTypeValue = criteria => {
  const rawValue = criteria?.certificationType ?? criteria?.CertificationType ?? null;
  const numericValue = Number(rawValue);

  if (Number.isNaN(numericValue)) {
    return null;
  }

  return numericValue;
};

export const isCriteriaOfCertificationType = (criteria, certificationType) => {
  const criteriaType = getCriteriaCertificationTypeValue(criteria);
  return criteriaType === Number(certificationType);
};

export const getDealerCountryId = dealerDetails => {
  if (!dealerDetails) {
    return null;
  }

  const region = dealerDetails.region ?? null;
  const rawCountryId = region?.country_Id ?? null;

  if (typeof rawCountryId !== 'string' || rawCountryId.trim().length === 0) {
    return null;
  }

  return rawCountryId.trim().toLowerCase();
};

export const getEligibleCriteriasForDealer = ({ criterias, dealerId, dealerDetails }) => {
  const normalizedCriterias = normalizeCollection(criterias);
  const dealerCountryId = getDealerCountryId(dealerDetails);
  const normalizedDealerId = typeof dealerId === 'string' ? dealerId.trim().toLowerCase() : null;

  return normalizedCriterias.filter(criteria => {
    const rawDealerIds = criteria?.dealerIds ?? criteria?.DealerIds ?? null;
    let dealerIds = Array.isArray(rawDealerIds) ? rawDealerIds : [];

    if (dealerIds.length === 0) {
      const fallbackDealers =
        criteria?.criteriaDealers ?? criteria?.CriteriaDealers ?? criteria?.dealers ?? criteria?.Dealers ?? [];

      if (Array.isArray(fallbackDealers) && fallbackDealers.length > 0) {
        dealerIds = fallbackDealers
          .map(dealer => dealer?.dealer_Id || dealer?.dealer?.id)
          .filter(value => typeof value === 'string' && value.trim().length > 0);
      }
    }

    const normalizedDealerIds = dealerIds.map(dealerIdValue => dealerIdValue.trim().toLowerCase());

    if (normalizedDealerIds.length > 0) {
      if (!normalizedDealerId) {
        return false;
      }

      if (normalizedDealerIds.includes(normalizedDealerId)) {
        return true;
      }
    }

    const directCountryList = criteria?.countryIds ?? null;
    let countryIds = Array.isArray(directCountryList) ? directCountryList : [];

    if (countryIds.length === 0) {
      const fallbackCountries =
        criteria?.criteriaCountries ?? criteria?.CriteriaCountries ?? criteria?.countries ?? criteria?.Countries ?? [];

      if (Array.isArray(fallbackCountries) && fallbackCountries.length > 0) {
        countryIds = fallbackCountries
          .map(country => country?.country_Id)
          .filter(value => typeof value === 'string' && value.trim().length > 0);
      }
    }

    const normalizedCountryIds = countryIds.map(countryId => countryId.trim().toLowerCase());

    if (normalizedCountryIds.length === 0 || !dealerCountryId) {
      return false;
    }

    return normalizedCountryIds.includes(dealerCountryId);
  });
};

export const getFirstCriteriaByType = (criterias, certificationType) => {
  const filtered = normalizeCollection(criterias).filter(criteria =>
    isCriteriaOfCertificationType(criteria, certificationType),
  );

  return (
    filtered.slice().sort((left, right) => {
      const leftOrder = Number(left?.order ?? left?.Order ?? 0);
      const rightOrder = Number(right?.order ?? right?.Order ?? 0);

      if (leftOrder !== rightOrder) {
        return leftOrder - rightOrder;
      }

      const leftName = String(left?.name ?? '');
      const rightName = String(right?.name ?? '');
      if (leftName !== rightName) {
        return leftName.localeCompare(rightName);
      }

      return String(left?.id ?? '').localeCompare(String(right?.id ?? ''));
    })[0] ?? null
  );
};

export const getLatestSubmissionForCriteria = (submissions, criteriaId) => {
  if (!criteriaId) {
    return null;
  }

  return getLatestSubmission(normalizeCollection(submissions).filter(submission => submission?.criteria_Id === criteriaId));
};

export const isRetailCriteria = criteria =>
  isCriteriaOfCertificationType(criteria, CriteriaCertificationTypeEnumValue.RetailEnvironment);

export const isExteriorCriteria = criteria =>
  isCriteriaOfCertificationType(criteria, CriteriaCertificationTypeEnumValue.ExteriorSignage);
